import React, { useState } from "react";
import { useMediaQuery } from "@material-ui/core";
import ActionDialog from "../ActionDialog";
import { useEnumStrings } from "../../enumStrings";

interface AutoApproveBannerContentToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="9" stroke="white" strokeWidth="1.5" fill="none" />
    <path
      d="M6 10L8.5 12.5L14 7"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DashCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="9" stroke="white" strokeWidth="1.5" fill="none" />
    <path
      d="M5 10H15"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const AutoApproveBannerContentToggle = ({
  checked,
  onChange,
  disabled = false,
}: AutoApproveBannerContentToggleProps) => {
  const enumStrings = useEnumStrings();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const handleClick = () => {
    if (disabled) return;
    setIsDialogOpen(true);
  };

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onChange(!checked);
    } finally {
      setIsConfirming(false);
      setIsDialogOpen(false);
    }
  };

  const handleClose = () => {
    if (isConfirming) return;
    setIsDialogOpen(false);
  };

  const enabledColor = "#00B621";
  const disabledColor = "#ED1C24";
  const statusColor = checked ? enabledColor : disabledColor;

  const willEnable = !checked;
  const dialogColor = willEnable ? enabledColor : disabledColor;

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          margin: "32px auto 0",
          maxWidth: "700px", /* REVERT: change back to "963px" */
        }}
      >
        {/* Description */}
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 500,
            fontSize: isMobile ? "14px" : "18px",
            lineHeight: isMobile ? "20px" : "160%",
            color: "#24272C",
            textAlign: "left",
            maxWidth: "898px",
            marginTop: "0px",
            marginBottom: "0px",
          }}
        >
          {checked ? (
            <>
              The category banners for this member currently requires chamber approval. If you don't want to review and approve all category banners uploaded by this member before they go live, toggle this setting to <span style={{ color: disabledColor, fontWeight: 700 }}>Not Required</span>.
            </>
          ) : (
            <>
              The category banners for this member currently do not require chamber approval. If you would like to review and approve all category banners uploaded by this member before they go live, toggle this setting to <span style={{ color: enabledColor, fontWeight: 700 }}>Required</span>.
            </>
          )}
        </p>

        {/* Auto Approve Toggle Bar */}
        <div
          onClick={handleClick}
          style={{
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            padding: "12px 16px",
            gap: "12px",
            width: "100%",
            minHeight: "50px",
            height: "auto",
            background: "#FFFFFF",
            border: "1px solid rgba(0, 0, 0, 0.5)",
            borderRadius: "12px",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
            userSelect: "none",
            marginTop: "28px",
            flexWrap: "wrap",
          }}
        >
          {/* Label */}
          <span
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 700,
              fontSize: isMobile ? "14px" : "18px",
              lineHeight: "150%",
              color: "#000000",
              display: "flex",
              alignItems: "center",
              whiteSpace: "nowrap",
            }}
          >
            {enumStrings.manageChamberEngage.categoryAdvertisingBanners.autoApproveBannerContentUpdates}
          </span>

          {/* Status pill */}
          <div
            style={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "4px 8px",
              gap: "12px",
              height: "34px",
              background: "#FFFFFF",
              border: `0.5px solid ${statusColor}`,
              borderRadius: "14.2px",
              flexShrink: 0,
            }}
          >
            {checked ? (
              <>
                {/* Enabled: text first, then icon */}
                <span
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 600,
                    fontSize: isMobile ? "13px" : "18px",
                    lineHeight: isMobile ? "18px" : "24px",
                    color: enabledColor,
                  }}
                >
                  {enumStrings.manageChamberEngage.categoryAdvertisingBanners.enabledStatus}
                </span>
                <div
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    backgroundColor: enabledColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircleIcon />
                </div>
              </>
            ) : (
              <>
                {/* Disabled: icon first, then text */}
                <div
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    backgroundColor: disabledColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DashCircleIcon />
                </div>
                <span
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 600,
                    fontSize: isMobile ? "13px" : "18px",
                    lineHeight: isMobile ? "18px" : "24px",
                    color: disabledColor,
                  }}
                >
                  {enumStrings.manageChamberEngage.categoryAdvertisingBanners.disabledStatus}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ActionDialog
        open={isDialogOpen}
        onClose={handleClose}
        icon={
          willEnable ? (
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="16" stroke="white" strokeWidth="2.5" fill="none" />
              <path d="M12 20L17.5 25.5L28 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="16" stroke="white" strokeWidth="2.5" fill="none" />
              <path d="M12 20H28" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          )
        }
        iconBgColor={dialogColor}
        title={
          willEnable
            ? enumStrings.manageChamberEngage.categoryAdvertisingBanners.enableAutoApproveTitle
            : enumStrings.manageChamberEngage.categoryAdvertisingBanners.disableAutoApproveTitle
        }
        description={
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              fontSize: "16px",
              lineHeight: "160%",
              color: "#24272C",
              margin: "0 0 4px",
            }}
            dangerouslySetInnerHTML={{
              __html: (willEnable
                ? enumStrings.manageChamberEngage.categoryAdvertisingBanners.enableAutoApproveMessage
                : enumStrings.manageChamberEngage.categoryAdvertisingBanners.disableAutoApproveMessage
              )
                .replace("<bold>", "<strong>")
                .replace("</bold>", "</strong>"),
            }}
          />
        }
        buttonText={
          willEnable
            ? enumStrings.manageChamberEngage.categoryAdvertisingBanners.enableButton
            : enumStrings.manageChamberEngage.categoryAdvertisingBanners.disableButton
        }
        onButtonClick={handleConfirm}
        buttonColor={dialogColor}
        maxWidth="420px"
      />
    </>
  );
};

export default AutoApproveBannerContentToggle;
